import { useEffect, useState, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '@/hooks/useSEO';
import BestCountryRecommender from './components/BestCountryRecommender';
import {
  ArrowRight, ArrowUpRight, Globe2, Calendar, Wallet, Clock, Briefcase,
  GraduationCap, AlertTriangle, Trophy, Languages, Banknote,
} from 'lucide-react';

/**
 * Visa Comparison Hub — `/study-abroad/visa-comparison/`
 * ──────────────────────────────────────────────────────
 * Side-by-side comparison of the four student-visa engines we maintain
 * (Germany / Australia / Canada / Ireland) across the dimensions that
 * actually drive the choice for Indian families:
 *   - 2026 financial threshold
 *   - Visa fee
 *   - Processing time
 *   - English-test floor
 *   - Work rights during study
 *   - Post-study visa duration
 *   - Key 2026 policy alerts
 *
 * Design: same `StudentVisaGuideShell` palette / typography / chip style so
 * it visually slots into the existing premium-consultant aesthetic without
 * forking the shell.
 */

const LAST_UPDATED = '31 May 2026';

const COUNTRIES = [
  {
    id: 'germany',
    name: 'Germany',
    flag: '🇩🇪',
    accent: '#000000',
    visaName: 'German Student Visa (National D-Visa)',
    href: '/study-abroad/germany/student-visa/',
    hubHref: '/study-abroad/germany/',
    financial: { amount: '€11,904', sub: 'Blocked account · 1 year', tone: 'gold' },
    visaFee:   { amount: '€75',     sub: 'Plus blocked-account setup' },
    processing:{ amount: '6–12 wks', sub: 'India embassy median 8 wks' },
    english:   { amount: 'IELTS 6.0+', sub: 'or DSH-2 / TestDaF for German-taught' },
    workStudy: { amount: '120 full / 240 half days', sub: 'per calendar year' },
    postStudy: { amount: '18 months', sub: 'Job-seeker residence permit' },
    keyAlert:  'Blocked-account threshold revised to €11,904 from Sep 2024',
  },
  {
    id: 'australia',
    name: 'Australia',
    flag: '🇦🇺',
    accent: '#012169',
    visaName: 'Subclass 500 Student Visa',
    href: '/study-abroad/australia/student-visa/',
    hubHref: '/study-abroad/australia/',
    financial: { amount: 'AUD 29,710', sub: 'Living costs · 12 months', tone: 'red' },
    visaFee:   { amount: 'AUD 1,600',  sub: 'Doubled from AUD 710 in Oct 2024' },
    processing:{ amount: '5–7 wks',    sub: '75% of Indian applicants in 2026' },
    english:   { amount: 'IELTS 6.0+', sub: 'PTE 50 / TOEFL iBT 64' },
    workStudy: { amount: '48 hrs / fortnight', sub: 'Unlimited during breaks' },
    postStudy: { amount: '2–4 years',  sub: 'Subclass 485 PSW · degree-dependent' },
    keyAlert:  'Genuine Student (GS) framework replaces GTE statement',
  },
  {
    id: 'canada',
    name: 'Canada',
    flag: '🇨🇦',
    accent: '#D52B1E',
    visaName: 'Study Permit',
    href: '/study-abroad/canada/student-visa/',
    hubHref: '/study-abroad/canada/',
    financial: { amount: 'CAD 20,635', sub: 'Outside Quebec · 12 months', tone: 'gold' },
    visaFee:   { amount: 'CAD 235',    sub: 'Includes biometrics fee' },
    processing:{ amount: '8–12 wks',   sub: 'SDS stream faster (4–6 wks)' },
    english:   { amount: 'IELTS 6.0+', sub: '6.0 each band for SDS stream' },
    workStudy: { amount: '24 hrs / week', sub: 'Up from 20 hrs · effective Nov 2024' },
    postStudy: { amount: '3 years',    sub: 'PGWP · master\u2019s/bachelor\u2019s eligible' },
    keyAlert:  'PAL (Provincial Attestation Letter) now mandatory',
  },
  {
    id: 'ireland',
    name: 'Ireland',
    flag: '🇮🇪',
    accent: '#169B62',
    visaName: 'Long Stay D Study Visa',
    href: '/study-abroad/ireland/student-visa/',
    hubHref: '/study-abroad/ireland/',
    financial: { amount: '€10,000',    sub: 'Living costs · per year', tone: 'gold' },
    visaFee:   { amount: '€60',        sub: 'Single-entry; €100 multi' },
    processing:{ amount: '8–10 wks',   sub: 'Plan 8–12 wks before intake' },
    english:   { amount: 'IELTS 6.0–6.5', sub: 'Course-specific minimums' },
    workStudy: { amount: '20 hrs / week', sub: '40 hrs in defined holidays' },
    postStudy: { amount: '1–2 years',  sub: 'Third Level Graduate Programme' },
    keyAlert:  '€6,000+ tuition pre-payment usually required pre-visa',
  },
];

const ROWS = [
  { key: 'financial',  label: 'Financial Threshold (12 months)', Icon: Wallet,        emphasis: true },
  { key: 'visaFee',    label: 'Visa Application Fee',            Icon: Banknote },
  { key: 'processing', label: 'Processing Time (India)',         Icon: Clock },
  { key: 'english',    label: 'English Test Floor',              Icon: Languages },
  { key: 'workStudy',  label: 'Work Rights While Studying',      Icon: Briefcase },
  { key: 'postStudy',  label: 'Post-Study Work Window',          Icon: Trophy },
];

const SectionHeading = ({ eyebrow, title, subtitle }) => (
  <div className="max-w-3xl mb-8 lg:mb-12">
    {eyebrow && (
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C5A028]/12 border border-[#C5A028]/35 text-[10px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-3">
        {eyebrow}
      </div>
    )}
    <h2 className="text-2xl sm:text-3xl lg:text-[36px] font-bold font-syne text-[#003087] leading-tight mb-3">{title}</h2>
    {subtitle && <p className="text-slate-600 leading-relaxed text-sm sm:text-base">{subtitle}</p>}
  </div>
);

const FaqItem = ({ q, a, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`border rounded-2xl transition-all ${open ? 'border-[#C5A028]/50 bg-white shadow-md' : 'border-slate-200 bg-white'}`} data-testid={`vc-faq-item-${index}`}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        className="w-full flex items-start justify-between gap-4 text-left px-5 sm:px-6 py-4 sm:py-5"
        data-testid={`vc-faq-toggle-${index}`}
      >
        <span className="text-sm sm:text-base font-semibold text-slate-900 leading-snug">{q}</span>
        <ArrowRight className={`shrink-0 w-5 h-5 mt-0.5 text-[#C5A028] transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-sm sm:text-[15px] text-slate-600 leading-relaxed">{a}</div>}
    </div>
  );
};

const FAQS = [
  {
    q: 'Which country has the lowest financial threshold for a student visa in 2026?',
    a: 'Ireland has the lowest published living-cost requirement at €10,000 per academic year. Germany sits at €11,904 (blocked account), Canada at CAD 20,635 (~€14K), and Australia is the highest at AUD 29,710 (~€18K). Tuition fees are separate in every case.',
  },
  {
    q: 'Which student visa is fastest to process from India?',
    a: 'Australia is the fastest at 5–7 weeks (median, 2026). Germany is 6–12 weeks, Canada is 8–12 weeks (faster via SDS), and Ireland is 8–10 weeks. Plan to lodge 10–12 weeks before intake for all four.',
  },
  {
    q: 'Which country offers the longest post-study work permit?',
    a: 'Canada\u2019s Post-Graduation Work Permit (PGWP) is up to 3 years. Australia\u2019s Subclass 485 is 2–4 years (degree-dependent). Germany offers 18 months job-search residence. Ireland\u2019s Third Level Graduate Programme is 1–2 years.',
  },
  {
    q: 'Which student visa allows the most weekly work hours?',
    a: 'Australia tops the list at 48 hours per fortnight in term + unlimited during breaks. Canada is now 24 hours per week (up from 20, since Nov 2024). Ireland is 20 hours per week in term, 40 hours during defined holidays. Germany is 120 full days OR 240 half days per calendar year.',
  },
  {
    q: 'Which country is best for Indian students in 2026?',
    a: 'There is no single best — it depends on your finances, course interest, and career plan. Ireland is best for students on a tight budget seeking EU post-study work. Australia and Canada lead on post-study migration pathways. Germany leads on tuition cost (often free) for STEM postgrads.',
  },
];

const VisaComparisonPage = () => {
  const [activeMobile, setActiveMobile] = useState('germany');

  // Page meta + canonical via the project-wide useSEO hook (so the global
  // canonical injector doesn't overwrite our values).
  usePageMeta(
    'Student Visa Comparison 2026: Germany vs Australia vs Canada vs Ireland | Zafco',
    'Compare student visas for Germany, Australia, Canada, and Ireland in 2026. Side-by-side financial thresholds, visa fees, processing times, work rights, and post-study options for Indian students.',
    { canonical: '/study-abroad/visa-comparison/' },
  );

  // JSON-LD: BreadcrumbList + FAQPage — auto-injected, cleaned on unmount
  useEffect(() => {
    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home',         item: 'https://zafco.in/' },
        { '@type': 'ListItem', position: 2, name: 'Study Abroad', item: 'https://zafco.in/study-abroad/' },
        { '@type': 'ListItem', position: 3, name: 'Visa Comparison' },
      ],
    };
    const faq = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
    };
    const bcEl  = document.createElement('script');
    const faqEl = document.createElement('script');
    bcEl.type  = 'application/ld+json'; bcEl.setAttribute('data-vc-bc-schema', '1');  bcEl.text  = JSON.stringify(breadcrumb);
    faqEl.type = 'application/ld+json'; faqEl.setAttribute('data-vc-faq-schema', '1'); faqEl.text = JSON.stringify(faq);
    document.head.appendChild(bcEl);
    document.head.appendChild(faqEl);
    return () => {
      document.head.querySelector('script[data-vc-bc-schema]')?.remove();
      document.head.querySelector('script[data-vc-faq-schema]')?.remove();
    };
  }, []);

  const activeCountry = COUNTRIES.find(c => c.id === activeMobile) || COUNTRIES[0];

  return (
    <div className="bg-white zaf-prose" data-testid="vc-page">
      {/* ============ HERO ============ */}
      <section
        className="relative overflow-hidden text-white"
        style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 55%, #020617 100%)' }}
      >
        <div aria-hidden="true" className="pointer-events-none absolute -top-32 -right-24 w-96 h-96 rounded-full bg-[#C5A028]/14 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 py-14 lg:py-24">
          <div className="max-w-3xl">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 backdrop-blur-sm text-[10.5px] font-bold tracking-[0.22em] text-[#FACC15] uppercase">
                <Globe2 className="w-3.5 h-3.5" /> Compare · 4 Destinations
              </span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-[10.5px] font-bold tracking-[0.22em] text-emerald-200 uppercase">
                <Calendar className="w-3.5 h-3.5" /> Updated: {LAST_UPDATED}
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-[52px] font-bold font-syne leading-[1.05] tracking-tight mb-5" data-testid="vc-h1">
              Student Visa Comparison: <span className="text-[#FACC15]">Germany · Australia · Canada · Ireland (2026)</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl mb-6">
              Six data points that actually drive the choice — <strong className="text-white">financial threshold</strong>, processing time, <strong className="text-white">work rights</strong>, and the post-study work window — across the four highest-demand destinations for Indian families in 2026.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/study-abroad/book-counselling/?source=study-abroad-visa-comparison-hero"
                className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5"
                data-testid="vc-hero-counselling"
              >
                Speak with a Visa Expert <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/study-abroad/free-profile-evaluation/?source=study-abroad-visa-comparison-hero"
                className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-semibold text-sm px-7 py-3.5 rounded-full transition-all"
              >
                Evaluate My Profile
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ INTERACTIVE RECOMMENDER (Best Country for Me quiz) ============ */}
      <BestCountryRecommender />

      {/* ============ DESKTOP COMPARISON GRID ============ */}
      <section className="bg-slate-50 py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="The 6-Data-Point Matrix"
            title="Side-by-Side Comparison Table"
            subtitle="Same six dimensions across all four destinations. Tap a country flag on mobile to swap columns."
          />

          {/* MOBILE: country switcher */}
          <div className="lg:hidden mb-5 grid grid-cols-4 gap-2" role="tablist" data-testid="vc-mobile-tabs">
            {COUNTRIES.map(c => (
              <button
                key={c.id}
                type="button"
                role="tab"
                aria-selected={activeMobile === c.id}
                onClick={() => setActiveMobile(c.id)}
                className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${
                  activeMobile === c.id
                    ? 'border-[#C5A028] bg-white shadow-md'
                    : 'border-slate-200 bg-white/60 hover:border-slate-300'
                }`}
                data-testid={`vc-mobile-tab-${c.id}`}
              >
                <span className="text-2xl">{c.flag}</span>
                <span className="text-[10px] font-bold tracking-wider uppercase text-slate-700">{c.name}</span>
              </button>
            ))}
          </div>

          {/* DESKTOP: 4-column grid */}
          <div className="hidden lg:grid grid-cols-[180px_repeat(4,1fr)] gap-px bg-slate-200 rounded-2xl overflow-hidden shadow-sm" data-testid="vc-desktop-grid">
            {/* Header row */}
            <div className="bg-[#003087] text-white px-4 py-5 flex items-center text-[11px] font-bold tracking-[0.18em] uppercase">
              Dimension
            </div>
            {COUNTRIES.map(c => (
              <Link
                key={c.id}
                to={c.href}
                className="bg-[#003087] hover:bg-[#001F5C] text-white px-4 py-5 transition-colors group flex flex-col gap-1"
                data-testid={`vc-country-header-${c.id}`}
              >
                <span className="text-2xl">{c.flag}</span>
                <span className="font-syne font-bold text-base leading-tight">{c.name}</span>
                <span className="text-[11px] text-slate-300 group-hover:text-[#FACC15] transition-colors flex items-center gap-1">
                  {c.visaName} <ArrowUpRight className="w-3 h-3" />
                </span>
              </Link>
            ))}

            {/* Data rows */}
            {ROWS.map(row => (
              <Fragment key={row.key}>
                <div
                  className={`px-4 py-5 flex items-center gap-2 text-sm font-semibold text-slate-700 ${
                    row.emphasis ? 'bg-amber-50' : 'bg-white'
                  }`}
                >
                  <row.Icon className={`w-4 h-4 ${row.emphasis ? 'text-amber-700' : 'text-[#C5A028]'} shrink-0`} />
                  <span className="leading-tight">{row.label}</span>
                </div>
                {COUNTRIES.map(c => {
                  const cell = c[row.key];
                  return (
                    <div
                      key={`${row.key}-${c.id}`}
                      className={`px-4 py-5 ${row.emphasis ? 'bg-amber-50' : 'bg-white'}`}
                      data-testid={`vc-cell-${c.id}-${row.key}`}
                    >
                      <div className={`font-bold font-syne text-lg leading-tight mb-1 ${
                        row.emphasis ? 'text-amber-900' : 'text-[#003087]'
                      }`}>
                        {cell.amount}
                      </div>
                      <div className="text-[11px] text-slate-500 leading-snug">{cell.sub}</div>
                    </div>
                  );
                })}
              </Fragment>
            ))}
          </div>

          {/* MOBILE: single-country card */}
          <div className="lg:hidden bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-200" data-testid="vc-mobile-card">
            <Link
              to={activeCountry.href}
              className="block bg-[#003087] text-white px-5 py-5"
            >
              <div className="flex items-start gap-3">
                <span className="text-3xl">{activeCountry.flag}</span>
                <div>
                  <div className="font-syne font-bold text-lg leading-tight">{activeCountry.name}</div>
                  <div className="text-xs text-slate-300 flex items-center gap-1 mt-0.5">
                    {activeCountry.visaName} <ArrowUpRight className="w-3 h-3" />
                  </div>
                </div>
              </div>
            </Link>
            <dl className="divide-y divide-slate-100">
              {ROWS.map(row => {
                const cell = activeCountry[row.key];
                return (
                  <div key={row.key} className={`px-5 py-4 ${row.emphasis ? 'bg-amber-50' : ''}`}>
                    <dt className="flex items-center gap-2 text-[11px] font-bold tracking-[0.16em] uppercase text-slate-500 mb-1">
                      <row.Icon className={`w-3.5 h-3.5 ${row.emphasis ? 'text-amber-700' : 'text-[#C5A028]'}`} />
                      {row.label}
                    </dt>
                    <dd>
                      <div className={`font-syne font-bold text-lg ${row.emphasis ? 'text-amber-900' : 'text-[#003087]'}`}>
                        {cell.amount}
                      </div>
                      <div className="text-xs text-slate-600 mt-0.5">{cell.sub}</div>
                    </dd>
                  </div>
                );
              })}
            </dl>
          </div>
        </div>
      </section>

      {/* ============ POLICY ALERTS ============ */}
      <section className="bg-white py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="2026 Compliance"
            title="Country-Specific Policy Alerts"
            subtitle="One non-negotiable rule that has changed in each country since 2023 — and that quietly drives refusal rates."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="vc-policy-alerts">
            {COUNTRIES.map(c => (
              <div
                key={c.id}
                className="rounded-2xl p-5 bg-gradient-to-br from-amber-50 to-amber-100/40 border border-amber-200/60 flex flex-col"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">{c.flag}</span>
                  <span className="font-syne font-bold text-[#003087] text-base">{c.name}</span>
                </div>
                <div className="flex items-start gap-2 mb-4">
                  <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-950 leading-relaxed">{c.keyAlert}</p>
                </div>
                <Link
                  to={c.href}
                  className="mt-auto inline-flex items-center gap-1 text-xs font-bold text-[#C5A028] hover:text-[#EAB308] transition-colors"
                  data-testid={`vc-policy-link-${c.id}`}
                >
                  Full {c.name} guide <ArrowUpRight className="w-3 h-3" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ MID CTA ============ */}
      <section className="bg-slate-50 py-10 lg:py-14">
        <div className="max-w-7xl mx-auto px-6">
          <div
            className="rounded-3xl p-6 sm:p-9 text-white relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 60%, #020617 100%)' }}
            data-testid="vc-mid-cta"
          >
            <div aria-hidden="true" className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#C5A028]/14 blur-3xl" />
            <div className="relative grid lg:grid-cols-[1fr_auto] gap-6 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C5A028]/15 border border-[#C5A028]/35 text-[10.5px] font-bold tracking-[0.2em] text-[#FACC15] uppercase mb-3">
                  Personalised Recommendation
                </div>
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold font-syne leading-tight mb-2">
                  Not sure which destination fits your profile?
                </h3>
                <p className="text-sm sm:text-[15px] text-slate-300 leading-relaxed max-w-xl">
                  Our counsellors map your academic background, finances, and career plan against the 2026 policies of all four countries — then recommend the visa with the highest approval probability for your specific case.
                </p>
              </div>
              <Link
                to="/study-abroad/book-counselling/?source=study-abroad-visa-comparison-mid-cta"
                className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm sm:text-base px-6 py-3.5 rounded-full transition-all hover:-translate-y-0.5 whitespace-nowrap"
                data-testid="vc-mid-cta-button"
              >
                Get Profile-Matched <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="bg-white py-14 lg:py-20">
        <div className="max-w-4xl mx-auto px-6">
          <SectionHeading
            eyebrow="Frequently Asked"
            title="Visa Comparison FAQs"
            subtitle="Schema-marked answers to the five most-asked head-to-head questions in 2026."
          />
          <div className="space-y-3" data-testid="vc-faqs">
            {FAQS.map((f, i) => <FaqItem key={f.q} q={f.q} a={f.a} index={i} />)}
          </div>
        </div>
      </section>

      {/* ============ RELATED RESOURCES ============ */}
      <section className="bg-slate-50 py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="Explore Further"
            title="Jump Into Each Country Guide"
            subtitle="The full visa file — financial breakdown, document checklist, step-by-step process, and FAQs — for each destination."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="vc-anchor-links">
            {COUNTRIES.map(c => (
              <Link
                key={c.id}
                to={c.href}
                className="group rounded-2xl p-5 bg-white border border-slate-200 hover:border-[#C5A028] hover:shadow-md transition-all"
                data-testid={`vc-anchor-${c.id}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-3xl">{c.flag}</span>
                  <div>
                    <div className="font-syne font-bold text-[#003087] text-base group-hover:text-[#C5A028] transition-colors leading-tight">
                      {c.name}
                    </div>
                    <div className="text-xs text-slate-500">{c.visaName}</div>
                  </div>
                </div>
                <div className="text-[11px] text-slate-500 leading-relaxed">
                  Full guide → documents, financials, 2026 process.
                </div>
                <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#C5A028]">
                  Read guide <ArrowRight className="w-3 h-3" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="text-white" style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 60%, #020617 100%)' }}>
        <div className="max-w-4xl mx-auto px-6 py-14 lg:py-20 text-center">
          <GraduationCap className="w-12 h-12 text-[#FACC15] mx-auto mb-5" />
          <h2 className="text-2xl md:text-4xl font-bold font-syne mb-4">
            Pick the right destination — with data, not guesswork.
          </h2>
          <p className="text-slate-300 max-w-2xl mx-auto mb-7 leading-relaxed">
            We don\u2019t just process applications. We map your academics, finances, and career intent against the 2026 visa policies of all four countries to recommend the highest-approval-probability path for your specific profile.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href={`https://wa.me/919990533327?text=${encodeURIComponent('Hello ZAFCO - I want help comparing student visas for Germany, Australia, Canada, and Ireland.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold px-7 py-3.5 rounded-full transition-colors"
              data-testid="vc-final-whatsapp"
            >
              WhatsApp Our Visa Team
            </a>
            <Link
              to="/study-abroad/book-counselling/?source=study-abroad-visa-comparison-final-cta"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white border border-white/20 font-semibold px-7 py-3.5 rounded-full transition-colors"
              data-testid="vc-final-counselling"
            >
              Book a Free Counselling Session
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VisaComparisonPage;
